/**
 * The solicitor brief — what the family discussion guide produces (owner, 2 Oct 2026).
 *
 * The guide used to be a page to read. It is now a set of questions the family answers together,
 * and this file turns those answers into a document they can hand to a solicitor: who is
 * instructing, the arrangement proposed, what the right should cover, what happens if things
 * change, how fairness to the other family is handled, and — honestly — what is still undecided,
 * listed as questions for the solicitor rather than hidden.
 *
 * It is a brief, not an agreement, and says so. Nothing here is advice: the figures are the
 * modelling tool's estimate and Services Australia decides; the agreement is the solicitor's work.
 * No provider or firm names in client-facing text.
 */
import type { GrannyFlatFamilyGuideAnswers } from '../../../../shared/src/calculation/factFindTypes.ts';

export interface GfSolicitorBriefFigures {
    /** Everything given for the right: cash + home transferred + the build. */
    entryContribution: number;
    /** What Centrelink values the right at — the formula, or the home / build provided. */
    interestValue: number;
    deprivation: number;
    isHomeowner: boolean;
    /** YYYY-MM-DD; blank = not yet set (today assumed in the modelling). */
    dateEntered: string;
}

export interface GfSolicitorBriefInput {
    clientName: string;
    partnerName?: string | null;
    answers: GrannyFlatFamilyGuideAnswers;
    /** Absent when the guide is opened before anything is modelled. */
    figures: GfSolicitorBriefFigures | null;
    /** ISO timestamp of generation. */
    generatedAt: string;
}

export const GF_ARRANGEMENT_FORM_LABELS: Record<NonNullable<GrannyFlatFamilyGuideAnswers['arrangementForm']>, string> = {
    cash: 'A cash payment to the family',
    build: 'Paying to build or extend accommodation on the family\'s property',
    transfer_home: 'Transferring the title of the current home to the family',
    buy_in_their_name: 'Buying a home in the family member\'s name',
    mixed: 'A mix: one of the above plus cash or other assets on top',
    undecided: 'Still to decide',
};

export const GF_BILLS_LABELS: Record<NonNullable<GrannyFlatFamilyGuideAnswers['billsPaidBy']>, string> = {
    me: 'The person moving in pays an agreed share of household bills',
    family: 'The family covers household bills',
    shared: 'Bills are shared on a basis still to be written down',
    undecided: 'Still to decide',
};

export const GF_CARE_LABELS: Record<NonNullable<GrannyFlatFamilyGuideAnswers['careProvided']>, string> = {
    yes: 'Yes — a family member will provide substantial daily care',
    no: 'No — company and support, not daily care',
    unsure: 'Not sure yet',
};

export const GF_FAIRNESS_LABELS: Record<NonNullable<GrannyFlatFamilyGuideAnswers['fairness']>, string> = {
    will: 'Adjust the Will so the other family members are balanced up',
    advance: 'Record what is given as an advance on that family member\'s inheritance',
    insurance: 'Use life insurance to top up the other family members',
    care_exchange: 'It is a fair exchange for the care being provided — write that understanding down',
    not_needed: 'Not needed — the family has agreed it is fair as it stands',
    undecided: 'Still to decide',
};

export const GF_WHAT_IF_LABELS = {
    ifHostSells: {
        question: 'If the family sells or moves',
        money_back: 'An agreed amount comes back, or the right moves to the new home',
        new_right: 'The right continues in whatever home the family moves to',
        undecided: 'Still to decide',
    },
    ifNeedCare: {
        question: 'If residential aged care is needed',
        money_back: 'An agreed amount is repaid to help meet care costs',
        nothing_back: 'Nothing is repaid — the money stays with the family',
        undecided: 'Still to decide',
    },
    ifRelationshipEnds: {
        question: 'If the relationship breaks down',
        money_back: 'An agreed amount is repaid and the arrangement ends',
        new_right: 'The right stays in place regardless',
        undecided: 'Still to decide',
    },
    ifHostDies: {
        question: 'If the family member dies, divorces or goes bankrupt',
        money_back: 'An agreed amount is repaid from the estate or the property',
        right_continues: 'The right binds whoever then owns the home',
        undecided: 'Still to decide',
    },
} as const;

const money = (v: number): string => `$${Math.round(v).toLocaleString('en-AU')}`;
const esc = (s: unknown): string => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const paragraphs = (s: string | undefined): string => (s || '').trim() ? (s as string).trim().split(/\n{2,}|\n/).map((l) => `<p>${esc(l)}</p>`).join('') : '';

function longDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** The items the family has not settled — each becomes a question for the solicitor. */
export function openQuestionsFor(a: GrannyFlatFamilyGuideAnswers): string[] {
    const q: string[] = [];
    if (!a.arrangementForm || a.arrangementForm === 'undecided') q.push('What form the payment for the right should take, and how it is best recorded.');
    if (!(a.includes || '').trim()) q.push('Exactly what the right to live there covers — rooms, shared areas, parking, garden, visitors.');
    if (!a.billsPaidBy || a.billsPaidBy === 'undecided') q.push('Who pays which household bills, and how that is written into the agreement.');
    if (!a.ifHostSells || a.ifHostSells === 'undecided') q.push(`${GF_WHAT_IF_LABELS.ifHostSells.question}: what happens to the right and to the money.`);
    if (!a.ifNeedCare || a.ifNeedCare === 'undecided') q.push(`${GF_WHAT_IF_LABELS.ifNeedCare.question}: whether any amount is repaid, and how it is calculated.`);
    if (!a.ifRelationshipEnds || a.ifRelationshipEnds === 'undecided') q.push(`${GF_WHAT_IF_LABELS.ifRelationshipEnds.question}: how the person moving in is protected.`);
    if (!a.ifHostDies || a.ifHostDies === 'undecided') q.push(`${GF_WHAT_IF_LABELS.ifHostDies.question}: whether the right binds the next owner, and what the estate owes.`);
    if (!a.fairness || a.fairness === 'undecided') q.push('How to keep things fair for other family members — the Will, an advance on inheritance, or otherwise.');
    return q;
}

export function buildGfSolicitorBriefHtml(input: GfSolicitorBriefInput): string {
    const a = input.answers || {};
    const who = input.partnerName ? `${input.clientName} and ${input.partnerName}` : input.clientName;
    const f = input.figures;
    const open = openQuestionsFor(a);
    const whatIf = (key: keyof typeof GF_WHAT_IF_LABELS) => {
        const def = GF_WHAT_IF_LABELS[key];
        const v = (a[key] || 'undecided') as keyof typeof def;
        const text = (def as Record<string, string>)[v] || def.undecided;
        const pending = !a[key] || a[key] === 'undecided';
        return `<tr><th scope="row">${esc(def.question)}</th><td${pending ? ' class="pending"' : ''}>${esc(text)}</td></tr>`;
    };

    return `<!DOCTYPE html>
<html lang="en-AU"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Granny flat agreement — instructions for our solicitor</title>
<style>
  :root{color-scheme:light}
  *{box-sizing:border-box}
  body{margin:0;background:#f8fafc;color:#0f172a;font:15px/1.55 -apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif}
  .page{max-width:820px;margin:0 auto;padding:40px 24px 64px}
  .doc{background:#fff;border:1px solid #e2e8f0;border-radius:16px;box-shadow:0 10px 30px rgba(15,23,42,.06);overflow:hidden}
  .doc::before{content:'';display:block;height:4px;background:linear-gradient(90deg,#2563eb,#14b8a6)}
  .inner{padding:36px 40px 40px}
  h1{margin:0 0 6px;font-size:26px;letter-spacing:-.02em;line-height:1.2}
  .lede{color:#334155;margin:0 0 24px;font-size:15px}
  h2{font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.08em;color:#64748b;margin:28px 0 10px}
  p{margin:0 0 8px}
  table{width:100%;border-collapse:collapse;margin:4px 0 8px}
  th,td{text-align:left;vertical-align:top;padding:9px 10px;border-bottom:1px solid #e2e8f0;font-size:14px}
  th[scope=row]{width:42%;font-weight:600;color:#334155}
  td.pending{color:#b45309;font-weight:600}
  td.num{text-align:right;font-variant-numeric:tabular-nums;font-weight:700}
  .note{border:1px solid #e2e8f0;border-left:4px solid #2563eb;border-radius:12px;padding:12px 14px;margin:12px 0;font-size:14px;color:#334155}
  .note--amber{border-left-color:#f59e0b}
  ol{padding-left:20px;margin:0}
  ol li{margin:0 0 6px}
  .foot{margin-top:28px;padding-top:14px;border-top:1px solid #e2e8f0;font-size:12.5px;color:#64748b}
  .actions{display:flex;gap:10px;justify-content:flex-end;margin:0 0 14px}
  .btn{border:1px solid #cbd5e1;background:#fff;border-radius:10px;padding:8px 14px;font-weight:600;font-size:14px;cursor:pointer}
  @media print{body{background:#fff}.page{padding:0}.doc{border:0;box-shadow:none;border-radius:0}.actions{display:none}}
</style></head>
<body><div class="page">
<div class="actions"><button class="btn" type="button" onclick="window.print()">Print or save as PDF</button></div>
<div class="doc"><div class="inner">
<h1>Granny flat agreement — instructions for our solicitor</h1>
<p class="lede">Prepared by ${esc(who)} on ${esc(longDate(input.generatedAt))}, from the family's own answers. This is a brief to start the conversation, not a draft agreement, and it is not legal advice. Please prepare the agreement and tell us what we have missed.</p>

<h2>Who is instructing</h2>
<table>
  <tr><th scope="row">The person${input.partnerName ? 's' : ''} moving in</th><td>${esc(who)}</td></tr>
  <tr><th scope="row">The family member${/,| and |&/.test(a.hostNames || '') ? 's' : ''} they would live with</th><td${(a.hostNames || '').trim() ? '' : ' class="pending"'}>${esc((a.hostNames || '').trim() || 'Still to confirm')}</td></tr>
</table>

<h2>The arrangement proposed</h2>
<table>
  <tr><th scope="row">What is given for the right to live there for life</th><td${!a.arrangementForm || a.arrangementForm === 'undecided' ? ' class="pending"' : ''}>${esc(GF_ARRANGEMENT_FORM_LABELS[a.arrangementForm || 'undecided'])}</td></tr>
  ${f ? `
  <tr><th scope="row">Intended start</th><td>${esc(f.dateEntered ? longDate(f.dateEntered) : 'As soon as the agreement is in place')}</td></tr>
  <tr><th scope="row">Amount being given (cash, home or build)</th><td class="num">${money(f.entryContribution)}</td></tr>
  <tr><th scope="row">Centrelink's value of the right (the tool's estimate)</th><td class="num">${money(f.interestValue)}</td></tr>
  <tr><th scope="row">Amount expected to be treated as a gift</th><td class="num">${f.deprivation > 0 ? money(f.deprivation) : 'None'}</td></tr>
  <tr><th scope="row">Expected Age Pension homeowner status</th><td>${f.isHomeowner ? 'Homeowner — the right is an exempt asset' : 'Non-homeowner — the amount paid is an assessable asset'}</td></tr>` : `
  <tr><th scope="row">Figures</th><td class="pending">Not yet modelled — the amount, Centrelink's value of the right and any gift will follow once the family has settled on a figure.</td></tr>`}
</table>
<div class="note">The pension figures are the modelling tool's estimate under the social security rules. Services Australia makes the assessment once the arrangement is reported to it; the agreement and evidence of what was paid (including any building contract and invoices) are what it will ask for.</div>

<h2>What the right should cover</h2>
<table>
  <tr><th scope="row">Rooms, shared areas and the rest</th><td${(a.includes || '').trim() ? '' : ' class="pending"'}>${(a.includes || '').trim() ? paragraphs(a.includes) : 'Still to decide'}</td></tr>
  <tr><th scope="row">Household bills and upkeep</th><td${!a.billsPaidBy || a.billsPaidBy === 'undecided' ? ' class="pending"' : ''}>${esc(GF_BILLS_LABELS[a.billsPaidBy || 'undecided'])}</td></tr>
  <tr><th scope="row">Daily care from the family</th><td>${esc(GF_CARE_LABELS[a.careProvided || 'unsure'])}</td></tr>
</table>

<h2>What happens if things change</h2>
<table>
  ${whatIf('ifHostSells')}
  ${whatIf('ifNeedCare')}
  ${whatIf('ifRelationshipEnds')}
  ${whatIf('ifHostDies')}
</table>
<div class="note note--amber">Where an amount is to come back, please advise how it should be set and recorded (for example as a loan repayable only in these events) so that it does not count as a financial asset for the pension in the meantime, and whether leaving within five years of starting could affect the pension assessment.</div>

<h2>Fairness to the rest of the family</h2>
<table>
  <tr><th scope="row">The family's approach</th><td${!a.fairness || a.fairness === 'undecided' ? ' class="pending"' : ''}>${esc(GF_FAIRNESS_LABELS[a.fairness || 'undecided'])}</td></tr>
</table>
<p>Please also advise whether the Will${input.partnerName ? 's' : ''} and any enduring power of attorney need updating alongside the agreement.</p>

${(a.notes || '').trim() ? `<h2>Notes from the family</h2>${paragraphs(a.notes)}` : ''}

<h2>Questions we would like you to answer</h2>
${open.length ? `<ol>${open.map((q) => `<li>${esc(q)}</li>`).join('')}</ol>` : '<p>The family has settled each of the points above; please tell us what we have missed.</p>'}
<ol start="${open.length + 1}">
  <li>How the right to live there for life is best protected — a registered interest on the title, a caveat, or otherwise.</li>
  <li>What the agreement should say about who may live there, visitors, and changes to the home.</li>
  <li>Any stamp duty, land tax or capital gains tax consequences of the chosen form, and who bears them.</li>
</ol>

<div class="foot">Produced from the granny flat family discussion guide. General information to help a family instruct a solicitor; it is not legal advice and does not replace the agreement the solicitor prepares.</div>
</div></div>
</div></body></html>`;
}

/** Open the brief in a new tab and download it, the way the advice documents are opened. */
export function openGfSolicitorBrief(input: GfSolicitorBriefInput): void {
    const html = buildGfSolicitorBriefHtml(input);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const safeName = input.clientName.replace(/[^a-zA-Z0-9]/g, '_') || 'Client';

    const tab = window.open('', '_blank');
    if (tab) {
        tab.document.open();
        tab.document.write(html);
        tab.document.close();
    } else {
        const openLink = document.createElement('a');
        openLink.href = url;
        openLink.target = '_blank';
        openLink.rel = 'noopener noreferrer';
        document.body.appendChild(openLink);
        openLink.click();
        document.body.removeChild(openLink);
    }
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = `GrannyFlat_SolicitorBrief_${safeName}_${input.generatedAt.slice(0, 10)}.html`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    setTimeout(() => URL.revokeObjectURL(url), 30000);
}
